'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Music, ExternalLink } from 'lucide-react';
import { MissingDataNotice } from '@/components/molecules/MissingDataNotice';
import { cn } from '@/components/atoms/primitives/utils';
import { toDisplayImageUrl } from '@/lib/image-proxy/toDisplayImageUrl';
import type { EventDetailArtist, EventDetailCategory } from '@/types/eventDetail';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ArtistSectionViewProps {
  artists: EventDetailArtist[];
  categories?: EventDetailCategory[];
  /** If true, artist names link to /artist/[id] */
  linkToArtistPage?: boolean;
  className?: string;
}

export type ArtistSectionProps = ArtistSectionViewProps;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const BIO_TRUNCATE_LENGTH = 120;

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function getCategoryColor(categories?: EventDetailCategory[]): string {
  const color = categories?.[0]?.color;
  if (color) return color;
  return 'var(--loci-artist-default)'; // default indigo
}

function isMusicCategory(categories?: EventDetailCategory[]): boolean {
  return categories?.some((c) => c.slug === 'music') ?? false;
}

function getBestImageUrl(artist: EventDetailArtist): string | null {
  const raw =
    artist.imageUrls?.medium ??
    artist.imageUrls?.large ??
    artist.imageUrls?.thumbnail ??
    artist.imageUrl ??
    null;
  // Provider URL → same-origin proxy; unknown host → '' → null (initials avatar).
  return toDisplayImageUrl(raw) || null;
}

function getDisplayGenres(artist: EventDetailArtist): string[] {
  if ((artist.genres ?? []).length > 0) return artist.genres ?? [];
  if (artist.genre) return [artist.genre];
  return [];
}

// ---------------------------------------------------------------------------
// Social link config
// ---------------------------------------------------------------------------

interface SocialLinkConfig {
  key: string;
  label: string;
  icon: React.ReactNode;
  className: string;
}

function SpotifyIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-4', className)}
      aria-hidden="true"
    >
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-4', className)}
      aria-hidden="true"
    >
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-4', className)}
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-4', className)}
      aria-hidden="true"
    >
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

const SOCIAL_LINKS: SocialLinkConfig[] = [
  // WEB-1065: Spotify green, Instagram pink and Facebook blue (and their hover
  // tints) are third-party brand colours, kept as literal hex on purpose.
  {
    key: 'spotify',
    label: 'Spotify',
    icon: <SpotifyIcon />,
    className: 'text-[#1DB954] hover:text-[#1ed760]',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    icon: <InstagramIcon />,
    className: 'text-[#E4405F] hover:text-[#f56084]',
  },
  {
    key: 'twitter',
    label: 'X (Twitter)',
    icon: <XIcon />,
    className: 'text-foreground hover:text-foreground/70',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    icon: <FacebookIcon />,
    className: 'text-[#1877F2] hover:text-[#4293f5]',
  },
  {
    key: 'website',
    label: 'Website',
    icon: <ExternalLink size={16} />,
    className: 'text-muted-foreground hover:text-foreground',
  },
];

// ---------------------------------------------------------------------------
// Artist Card
// ---------------------------------------------------------------------------

function ArtistCard({
  artist,
  categoryColor,
  linkToArtistPage,
}: {
  artist: EventDetailArtist;
  categoryColor: string;
  linkToArtistPage?: boolean;
}) {
  const [bioExpanded, setBioExpanded] = useState(false);
  const imageUrl = getBestImageUrl(artist);
  const genres = getDisplayGenres(artist);
  const hasSocialLinks =
    artist.socialLinks &&
    Object.values(artist.socialLinks).some((v) => v !== null && v !== undefined);
  const isSparse = !imageUrl && !artist.description;

  const shouldTruncateBio =
    artist.description != null && artist.description.length > BIO_TRUNCATE_LENGTH;
  const displayBio =
    artist.description && shouldTruncateBio && !bioExpanded
      ? `${artist.description.slice(0, BIO_TRUNCATE_LENGTH)}...`
      : artist.description;

  const nameContent = (
    <h3 className="text-sm font-semibold text-foreground leading-tight">{artist.name}</h3>
  );

  return (
    <div
      data-testid="artist-card"
      className="flex flex-col items-center gap-3 rounded-lg border border-border/50 bg-card p-4 text-center"
    >
      {/* Avatar */}
      {imageUrl ? (
        <div className="relative size-20 shrink-0 overflow-hidden rounded-full">
          <Image src={imageUrl} alt={artist.name} fill className="object-cover" sizes="80px" />
        </div>
      ) : (
        <div
          className="flex size-20 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white"
          style={{ backgroundColor: categoryColor }}
          aria-hidden="true"
          data-testid="artist-initials-avatar"
        >
          {getInitials(artist.name)}
        </div>
      )}

      {/* Name */}
      {linkToArtistPage ? (
        <Link
          href={`/artist/${artist.id}`}
          className="inline-flex min-h-11 desktop:min-h-0 items-center rounded-sm px-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          data-testid="artist-link"
        >
          {nameContent}
        </Link>
      ) : (
        nameContent
      )}

      {/* Genres */}
      {genres.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1">
          {genres.map((g) => (
            <span
              key={g}
              className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
            >
              {g}
            </span>
          ))}
        </div>
      )}

      {/* Bio */}
      {artist.description ? (
        <div className="w-full">
          <p className="text-xs text-muted-foreground leading-relaxed">{displayBio}</p>
          {shouldTruncateBio && (
            <button
              type="button"
              onClick={() => setBioExpanded((prev) => !prev)}
              className="mt-1 inline-flex min-h-11 desktop:min-h-0 items-center rounded-sm px-2 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={bioExpanded ? 'Show less' : 'Read more'}
            >
              {bioExpanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      ) : isSparse ? (
        <MissingDataNotice variant="inline" field="Bio" />
      ) : null}

      {/* Social Links */}
      {hasSocialLinks && (
        <div className="flex items-center gap-2" data-testid="artist-social-links">
          {SOCIAL_LINKS.map(({ key, label, icon, className: linkClassName }) => {
            const url = artist.socialLinks?.[key as keyof typeof artist.socialLinks];
            if (!url) return null;
            return (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'inline-flex size-11 desktop:size-9 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  linkClassName,
                )}
                aria-label={`${artist.name} on ${label}`}
              >
                {icon}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// View (pure)
// ---------------------------------------------------------------------------

export function ArtistSectionView({
  artists,
  categories,
  linkToArtistPage,
  className,
}: ArtistSectionViewProps) {
  if (artists.length === 0) {
    return null;
  }

  const categoryColor = getCategoryColor(categories);
  const sectionTitle = isMusicCategory(categories) ? 'Lineup' : 'Performers';

  return (
    <section
      aria-label={sectionTitle}
      data-testid="artist-section"
      className={cn('w-full', className)}
    >
      <div className="mb-4 flex items-center gap-2">
        <Music size={18} className="text-muted-foreground" aria-hidden="true" />
        <h2 className="text-base font-semibold text-foreground">{sectionTitle}</h2>
      </div>
      <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 tablet:grid tablet:grid-cols-2 tablet:overflow-visible tablet:pb-0 desktop:grid-cols-3">
        {artists.map((artist) => (
          <div key={artist.id} className="min-w-[280px] snap-start tablet:min-w-0">
            <ArtistCard
              artist={artist}
              categoryColor={categoryColor}
              linkToArtistPage={linkToArtistPage}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Connected (data-fetching wrapper — currently pass-through)
// ---------------------------------------------------------------------------

export function ArtistSection(props: ArtistSectionProps) {
  return <ArtistSectionView {...props} />;
}
